import { type MessageKeys, type Messages, type NestedKeyOf, type NestedValueOf, useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

import type { FuelType, RunState } from '~/data/api/client/ders'
import type {
  BooleanLogic,
  Condition,
  DurationUnit,
  EventType,
  FlatConditionSpec,
  GroupedConditionSpec,
  LogLevel,
  NotificationPriority,
  RelativeUnit,
  RunReportParametersAuthoring,
  WindowUnit
} from '~/data/api/client/workflows'
import {
  type ActionId,
  type AttributeKeyFor,
  type CatalogAttribute,
  type CatalogOperator,
  type CatalogTriggerPickerEntry,
  type ContextEntityKind,
  type ContextEnumValueFor,
  type ContextFieldKeyFor,
  type DerFaultAssetType,
  EVENT_ATTRIBUTE_PREFIX,
  type EventCatalogMessageKey,
  type TriggerPickerEntry,
  type TriggerPickerEntryKey,
  type TriggerPickerSubject,
  attributeKeyFromField,
  getEventType,
  pickerEntryFor,
  wholeReferencePath
} from '~/data/api/client/workflows/catalog'

import type { WorkflowTemplateCategory } from '~/app/workflows/components/templates/presets'

import { useGeneratorFuelTypeFormatter } from './fuel'

/**
 * `EventType` ids carry dots (`charger.status_notification`), which next-intl
 * reads as message-key nesting — so each id maps to a flat, dot-free key under
 * the `workflows.eventCatalog` namespace. `satisfies Record<EventType, …>` makes
 * a newly added event type a compile error until its label key is registered
 * here (and in the locale files).
 */
const EVENT_TYPE_LABEL_KEY = {
  'charger.status_notification': 'eventType_chargerStatusNotification',
  'charger.start_transaction': 'eventType_chargerStartTransaction',
  'charger.stop_transaction': 'eventType_chargerStopTransaction',
  'charger.boot_notification': 'eventType_chargerBootNotification',
  'charger.data_transfer': 'eventType_chargerDataTransfer',
  'charger.transaction_summary': 'eventType_chargerTransactionSummary',
  'vehicle.telemetry': 'eventType_vehicleTelemetry',
  'vehicle.fault': 'eventType_vehicleFault',
  'der.fault': 'eventType_derFault',
  'site.power_utilization': 'eventType_sitePowerUtilization',
  'entity.connectivity': 'eventType_entityConnectivity',
  'condition.opened': 'eventType_conditionOpened',
  'condition.resolved': 'eventType_conditionResolved',
  scheduled: 'eventType_scheduled'
} as const satisfies Record<EventType, string>

/** One-line description keys, mirroring {@link EVENT_TYPE_LABEL_KEY}. */
const EVENT_TYPE_DESCRIPTION_KEY = {
  'charger.status_notification': 'eventTypeDescription_chargerStatusNotification',
  'charger.start_transaction': 'eventTypeDescription_chargerStartTransaction',
  'charger.stop_transaction': 'eventTypeDescription_chargerStopTransaction',
  'charger.boot_notification': 'eventTypeDescription_chargerBootNotification',
  'charger.data_transfer': 'eventTypeDescription_chargerDataTransfer',
  'charger.transaction_summary': 'eventTypeDescription_chargerTransactionSummary',
  'vehicle.telemetry': 'eventTypeDescription_vehicleTelemetry',
  'vehicle.fault': 'eventTypeDescription_vehicleFault',
  'der.fault': 'eventTypeDescription_derFault',
  'site.power_utilization': 'eventTypeDescription_sitePowerUtilization',
  'entity.connectivity': 'eventTypeDescription_entityConnectivity',
  'condition.opened': 'eventTypeDescription_conditionOpened',
  'condition.resolved': 'eventTypeDescription_conditionResolved',
  scheduled: 'eventTypeDescription_scheduled'
} as const satisfies Record<EventType, string>

/** Resolves a workflow trigger event type to its localized display label. */
function useEventTypeFormatter() {
  const t = useTranslations('workflows.eventCatalog')
  return useCallback((eventType: EventType) => {
    // Definitions arrive from the API as raw strings with no runtime enum check, so a
    // backend-added or legacy event type may be absent from the map; show a generic
    // label rather than calling t(undefined).
    const key = EVENT_TYPE_LABEL_KEY[eventType] as (typeof EVENT_TYPE_LABEL_KEY)[EventType] | undefined
    return key ? t(key) : t('eventType_unknown')
  }, [t])
}

/** Resolves a workflow trigger event type to its localized one-line description. */
function useEventTypeDescriptionFormatter() {
  const t = useTranslations('workflows.eventCatalog')
  return useCallback((eventType: EventType) => {
    const key = EVENT_TYPE_DESCRIPTION_KEY[eventType] as (typeof EVENT_TYPE_DESCRIPTION_KEY)[EventType] | undefined
    // Unknown event types have no authored description; empty reads better than
    // a placeholder in the tight picker/inspector layouts.
    return key ? t(key) : ''
  }, [t])
}

/**
 * Cadence keys, mirroring {@link EVENT_TYPE_LABEL_KEY}.
 *
 * Whether an event arrives once or repeatedly is the distinction the catalog
 * hides most effectively and the one that decides whether a workflow sends a
 * single message or a hundred — a session's totals are re-sent on every meter
 * reading, telemetry streams per vehicle. It gets its own line in the picker's
 * detail pane rather than a clause inside the description, where it kept losing
 * to the description's job of saying what the event *is*.
 *
 * Keyed by event rather than by card: an event's cadence is a property of the
 * feed, so both not-heard-from cards share one line and it names no entity kind.
 */
const EVENT_TYPE_FIRES_KEY = {
  'charger.status_notification': 'eventTypeFires_chargerStatusNotification',
  'charger.start_transaction': 'eventTypeFires_chargerStartTransaction',
  'charger.stop_transaction': 'eventTypeFires_chargerStopTransaction',
  'charger.boot_notification': 'eventTypeFires_chargerBootNotification',
  'charger.data_transfer': 'eventTypeFires_chargerDataTransfer',
  'charger.transaction_summary': 'eventTypeFires_chargerTransactionSummary',
  'vehicle.telemetry': 'eventTypeFires_vehicleTelemetry',
  'vehicle.fault': 'eventTypeFires_vehicleFault',
  'der.fault': 'eventTypeFires_derFault',
  'site.power_utilization': 'eventTypeFires_sitePowerUtilization',
  'entity.connectivity': 'eventTypeFires_entityConnectivity',
  'condition.opened': 'eventTypeFires_conditionOpened',
  'condition.resolved': 'eventTypeFires_conditionResolved',
  scheduled: 'eventTypeFires_scheduled'
} as const satisfies Record<EventType, string>

/**
 * Pointers to the sibling an author probably wanted, for the cards that are
 * genuinely confusable with another.
 *
 * Keyed by card rather than by event, unlike {@link EVENT_TYPE_FIRES_KEY} above.
 * Cadence is a property of the feed — the connectivity feed reports a silence
 * the same way whichever kind goes quiet — but which sibling you probably wanted
 * depends on what you are watching. "Use Charger boot to catch it coming back"
 * is the right pointer from the charger card and nonsense from the SynopLink
 * one, which has no boot event to be confused with.
 *
 * Deliberately partial, and it must stay that way: a "compare with" line on a
 * card that has no confusable twin is noise, and inventing one for every card
 * would bury the few that matter. `charger.stop_transaction` is the important
 * case — it fires at the end of a session but carries no energy or duration, so
 * an author reaching for session totals needs sending to
 * `charger.transaction_summary`.
 */
const PICKER_ENTRY_COMPARISON_KEY: Partial<Record<TriggerPickerEntryKey, EventCatalogMessageKey>> = {
  'charger.start_transaction': 'eventTypeCompare_chargerStartTransaction',
  'charger.stop_transaction': 'eventTypeCompare_chargerStopTransaction',
  'charger.transaction_summary': 'eventTypeCompare_chargerTransactionSummary',
  'charger.boot_notification': 'eventTypeCompare_chargerBootNotification',
  'notHeardFrom.charger': 'eventTypeCompare_chargerNotHeardFrom'
}

/**
 * Cadence lines a card states in place of its event's: the fault cards each watch
 * one kind of asset, so each says which. Deliberately partial — every other card
 * reads its event's line, which already names what it watches.
 */
const PICKER_ENTRY_FIRES_KEY: Partial<Record<TriggerPickerEntryKey, EventCatalogMessageKey>> = {
  'der.fault.bess': 'eventTypeFires_bessFault',
  'der.fault.genset': 'eventTypeFires_generatorFault'
}

/** Resolves a picker card to its localized cadence line ("when it fires"). */
function useTriggerCadenceFormatter() {
  const t = useTranslations('workflows.eventCatalog')
  return useCallback(
    (entry: CatalogTriggerPickerEntry) => {
      const key = PICKER_ENTRY_FIRES_KEY[entry.key] ?? (EVENT_TYPE_FIRES_KEY[entry.eventType] as EventCatalogMessageKey | undefined)
      return key ? t(key) : ''
    },
    [t]
  )
}

/**
 * Resolves a picker card to its localized "use X instead" pointer, or `null`
 * where the card has no confusable sibling.
 */
function useTriggerComparisonFormatter() {
  const t = useTranslations('workflows.eventCatalog')
  return useCallback(
    (entryKey: TriggerPickerEntryKey) => {
      const key = PICKER_ENTRY_COMPARISON_KEY[entryKey]
      return key ? t(key) : null
    },
    [t]
  )
}

/**
 * The card a label or description comes from: the card itself when handed one —
 * the picker's rows are cards, and a card's pinned kind is its own `assetType`
 * rather than a subject's `assetTypes` — else the card a trigger was authored from.
 */
function cardFor(trigger: TriggerPickerSubject | TriggerPickerEntry): TriggerPickerEntry | undefined {
  return 'labelKey' in trigger ? trigger : pickerEntryFor(trigger)
}

/**
 * A trigger's title — the picker card it was authored from, which is what the
 * card, the canvas node, and the inspector header all show. Taking the card
 * rather than the event type is what lets the two connectivity cards read
 * "Charger not heard from" and "Link not heard from" off one feed, and the two
 * fault cards "BESS fault" and "Generator fault".
 *
 * A trigger no card describes — an event outside the catalog, or a persisted
 * connectivity kind whose card has yet to be written — falls back to the event's
 * own label, so it still names itself.
 */
function useTriggerLabelFormatter() {
  const t = useTranslations('workflows.eventCatalog')
  const eventTypeLabel = useEventTypeFormatter()
  return useCallback(
    (trigger: TriggerPickerSubject | TriggerPickerEntry) => {
      const entry = cardFor(trigger)
      if (entry) return t(entry.labelKey)
      return trigger.eventType ? eventTypeLabel(trigger.eventType) : ''
    },
    [t, eventTypeLabel]
  )
}

/** A trigger's one-line description, resolved the same way as {@link useTriggerLabelFormatter}. */
function useTriggerDescriptionFormatter() {
  const t = useTranslations('workflows.eventCatalog')
  const eventTypeDescription = useEventTypeDescriptionFormatter()
  return useCallback(
    (trigger: TriggerPickerSubject | TriggerPickerEntry) => {
      const entry = cardFor(trigger)
      if (entry) return t(entry.descriptionKey)
      return trigger.eventType ? eventTypeDescription(trigger.eventType) : ''
    },
    [t, eventTypeDescription]
  )
}

/**
 * Every valid leaf key under the `workflows.eventAttributes` namespace —
 * composed from the same `use-intl` utilities (`MessageKeys`/`NestedKeyOf`/
 * `NestedValueOf`) that `useTranslations` itself uses internally to type `t`.
 * Typing the map's values against this (rather than `string`) means a typo'd
 * or renamed value is a compile error at the map definition, and `t(key)`
 * below needs no cast.
 */
type EventAttributeMessageKey = MessageKeys<
  NestedValueOf<Messages, 'workflows.eventAttributes'>,
  NestedKeyOf<NestedValueOf<Messages, 'workflows.eventAttributes'>>
>

/**
 * The label map's entry for one event.
 *
 * `Record<never, …>` collapses to `{}`, which accepts any object — so an event whose
 * schema is empty (or whose last attribute was removed) would take arbitrary labels
 * silently. The `never` branch is spelled `Record<PropertyKey, never>` instead: `{}`
 * still satisfies it, while any property fails because no value is a `never`.
 *
 * `[…] extends […]` wraps both sides so a naked `never` is compared rather than
 * distributed over, which would otherwise short-circuit the whole conditional.
 */
type AttributeLabels<E extends EventType> = [AttributeKeyFor<E>] extends [never]
  ? Record<PropertyKey, never>
  : Record<AttributeKeyFor<E>, EventAttributeMessageKey>

/**
 * Attribute labels, unlike event-type labels, are scoped per event: the same
 * bare key can mean something different on another event, so lookup nests
 * event type → attribute key → locale key rather than flattening like
 * `EVENT_TYPE_LABEL_KEY`.
 *
 * Keyed by `AttributeKeyFor<E>` — the literal attribute keys that event declares in
 * `EVENT_CATALOG` — so this map must cover every catalog attribute exactly: omit one
 * and it is a missing property naming the key, add one the catalog does not declare
 * and it is an excess property. The outer `EventType` mapping makes a newly added
 * event type a compile error until it is registered. Events with no verified schema
 * resolve to `Record<never, …>`, satisfied by `{}`.
 *
 * The runtime lookup in `useEventAttributeFormatter` widens at the call site rather
 * than here: widening the declaration to `Record<string, …>` would serve that one
 * index at the cost of every key check above.
 */
const EVENT_ATTRIBUTE_LABEL_KEY: { [E in EventType]: AttributeLabels<E> } = {
  'charger.status_notification': {
    charger_id: 'chargerStatusNotification_chargerId',
    charger_name: 'chargerStatusNotification_chargerName',
    connector_id: 'chargerStatusNotification_connectorId',
    site_id: 'chargerStatusNotification_siteId',
    organization_id: 'chargerStatusNotification_organizationId',
    status: 'chargerStatusNotification_status',
    error_code: 'chargerStatusNotification_errorCode',
    vendor_error_code: 'chargerStatusNotification_vendorErrorCode',
    error_info: 'chargerStatusNotification_errorInfo',
    severity: 'chargerStatusNotification_severity',
    timestamp: 'chargerStatusNotification_timestamp'
  },
  'charger.start_transaction': {
    id: 'chargerStartTransaction_id',
    charger_id: 'chargerStartTransaction_chargerId',
    charger_name: 'chargerStartTransaction_chargerName',
    connector_id: 'chargerStartTransaction_connectorId',
    site_id: 'chargerStartTransaction_siteId',
    organization_id: 'chargerStartTransaction_organizationId',
    ocpp_tag: 'chargerStartTransaction_ocppTag',
    start_timestamp: 'chargerStartTransaction_startTimestamp'
  },
  'charger.stop_transaction': {
    id: 'chargerStopTransaction_id',
    charger_id: 'chargerStopTransaction_chargerId',
    charger_name: 'chargerStopTransaction_chargerName',
    connector_id: 'chargerStopTransaction_connectorId',
    site_id: 'chargerStopTransaction_siteId',
    organization_id: 'chargerStopTransaction_organizationId',
    ocpp_tag: 'chargerStopTransaction_ocppTag',
    stop_timestamp: 'chargerStopTransaction_stopTimestamp',
    stop_reason: 'chargerStopTransaction_stopReason',
    stop_event_actor: 'chargerStopTransaction_stopEventActor'
  },
  'charger.boot_notification': {
    charger_id: 'chargerBootNotification_chargerId',
    charger_name: 'chargerBootNotification_chargerName',
    site_id: 'chargerBootNotification_siteId',
    organization_id: 'chargerBootNotification_organizationId',
    charge_point_vendor: 'chargerBootNotification_chargePointVendor',
    charge_point_model: 'chargerBootNotification_chargePointModel',
    charge_point_serial_number: 'chargerBootNotification_chargePointSerialNumber',
    charge_box_serial_number: 'chargerBootNotification_chargeBoxSerialNumber',
    firmware_version: 'chargerBootNotification_firmwareVersion',
    iccid: 'chargerBootNotification_iccid',
    imsi: 'chargerBootNotification_imsi',
    meter_type: 'chargerBootNotification_meterType',
    meter_serial_number: 'chargerBootNotification_meterSerialNumber',
    timestamp: 'chargerBootNotification_timestamp'
  },
  'charger.data_transfer': {
    charger_id: 'chargerDataTransfer_chargerId',
    charger_name: 'chargerDataTransfer_chargerName',
    site_id: 'chargerDataTransfer_siteId',
    organization_id: 'chargerDataTransfer_organizationId',
    vendor_id: 'chargerDataTransfer_vendorId',
    message_id: 'chargerDataTransfer_messageId',
    request_id: 'chargerDataTransfer_requestId',
    timestamp: 'chargerDataTransfer_timestamp'
  },
  'charger.transaction_summary': {
    charger_id: 'chargerTransactionSummary_chargerId',
    message_type: 'chargerTransactionSummary_messageType',
    transaction_id: 'chargerTransactionSummary_transactionId',
    organization_id: 'chargerTransactionSummary_organizationId',
    site_id: 'chargerTransactionSummary_siteId',
    node_id: 'chargerTransactionSummary_nodeId',
    connector_id: 'chargerTransactionSummary_connectorId',
    ocpp_tag: 'chargerTransactionSummary_ocppTag',
    start_timestamp: 'chargerTransactionSummary_startTimestamp',
    stop_timestamp: 'chargerTransactionSummary_stopTimestamp',
    stop_reason: 'chargerTransactionSummary_stopReason',
    stop_event_actor: 'chargerTransactionSummary_stopEventActor',
    plug_in_time: 'chargerTransactionSummary_plugInTime',
    plug_out_time: 'chargerTransactionSummary_plugOutTime',
    total_energy_imported: 'chargerTransactionSummary_totalEnergyImported',
    total_energy_exported: 'chargerTransactionSummary_totalEnergyExported',
    mean_power_import: 'chargerTransactionSummary_meanPowerImport',
    max_power_import: 'chargerTransactionSummary_maxPowerImport',
    latest_power_import: 'chargerTransactionSummary_latestPowerImport',
    starting_soc: 'chargerTransactionSummary_startingSoc',
    latest_soc: 'chargerTransactionSummary_latestSoc',
    max_voltage: 'chargerTransactionSummary_maxVoltage',
    min_voltage: 'chargerTransactionSummary_minVoltage',
    timestamp: 'chargerTransactionSummary_timestamp'
  },
  'vehicle.telemetry': {
    vehicle_id: 'vehicleTelemetry_vehicleId',
    organization_id: 'vehicleTelemetry_organizationId',
    fleet_id: 'vehicleTelemetry_fleetId',
    site_id: 'vehicleTelemetry_siteId',
    vehicle_status: 'vehicleTelemetry_vehicleStatus',
    state_of_charge: 'vehicleTelemetry_stateOfCharge',
    latitude: 'vehicleTelemetry_latitude',
    longitude: 'vehicleTelemetry_longitude',
    speed_kph: 'vehicleTelemetry_speedKph',
    odometer_km: 'vehicleTelemetry_odometerKm',
    remaining_range_km: 'vehicleTelemetry_remainingRangeKm',
    distance_until_home_km: 'vehicleTelemetry_distanceUntilHomeKm',
    time_until_home_minutes: 'vehicleTelemetry_timeUntilHomeMinutes',
    source_provider: 'vehicleTelemetry_sourceProvider',
    timestamp: 'vehicleTelemetry_timestamp'
  },
  'vehicle.fault': {
    vehicle_id: 'vehicleFault_vehicleId',
    organization_id: 'vehicleFault_organizationId',
    provider: 'vehicleFault_provider',
    spn: 'vehicleFault_spn',
    spn_name: 'vehicleFault_spnName',
    spn_description: 'vehicleFault_spnDescription',
    fmi: 'vehicleFault_fmi',
    fmi_text: 'vehicleFault_fmiText',
    source_address: 'vehicleFault_sourceAddress',
    source_name: 'vehicleFault_sourceName',
    source_label: 'vehicleFault_sourceLabel',
    occurrence_count: 'vehicleFault_occurrenceCount',
    mil_lamp_on: 'vehicleFault_milLampOn',
    amber_lamp_on: 'vehicleFault_amberLampOn',
    red_lamp_on: 'vehicleFault_redLampOn',
    protect_lamp_on: 'vehicleFault_protectLampOn',
    dtc_id: 'vehicleFault_dtcId',
    dtc_short_code: 'vehicleFault_dtcShortCode',
    dtc_description: 'vehicleFault_dtcDescription',
    latitude: 'vehicleFault_latitude',
    longitude: 'vehicleFault_longitude',
    timestamp: 'vehicleFault_timestamp'
  },
  'der.fault': {
    der_asset_id: 'derFault_derAssetId',
    asset_name: 'derFault_assetName',
    asset_type: 'derFault_assetType',
    organization_id: 'derFault_organizationId',
    site_id: 'derFault_siteId',
    location: 'derFault_location',
    alarm_code: 'derFault_alarmCode',
    alarm_reason: 'derFault_alarmReason',
    severity: 'derFault_severity',
    transition: 'derFault_transition',
    timestamp: 'derFault_timestamp'
  },
  'site.power_utilization': {
    site_id: 'sitePowerUtilization_siteId',
    site_name: 'sitePowerUtilization_siteName',
    organization_id: 'sitePowerUtilization_organizationId',
    utilization_kw: 'sitePowerUtilization_utilizationKw',
    limit_kw: 'sitePowerUtilization_limitKw'
  },
  'entity.connectivity': {
    entity_type: 'entityConnectivity_entityType',
    entity_id: 'entityConnectivity_entityId',
    organization_id: 'entityConnectivity_organizationId',
    site_id: 'entityConnectivity_siteId',
    last_heard_at: 'entityConnectivity_lastHeardAt',
    active: 'entityConnectivity_active'
  },
  'condition.opened': {},
  'condition.resolved': {},
  scheduled: {}
}

/**
 * Labels a DER fault trigger's attributes take once its asset kind is known: the
 * feed's subject fields name "the asset", and a trigger that watches one kind
 * names that kind instead. Fields that read the same whichever kind raised the
 * fault keep the event's own label.
 */
const DER_FAULT_ATTRIBUTE_LABEL_KEY = {
  BESS: { der_asset_id: 'derFault_bessId', asset_name: 'derFault_bessName' },
  GENSET: { der_asset_id: 'derFault_generatorId', asset_name: 'derFault_generatorName' }
} as const satisfies Record<DerFaultAssetType, Partial<Record<AttributeKeyFor<'der.fault'>, EventAttributeMessageKey>>>

/**
 * Resolves a workflow trigger attribute (scoped to its event type) to its
 * localized display label under `workflows.eventAttributes`. Every attribute the
 * catalog declares has a label, enforced by `EVENT_ATTRIBUTE_LABEL_KEY`'s type, so
 * the raw-key fallback is reachable only for a key outside the catalog: an event
 * with no attribute schema, or a stored definition naming an attribute since
 * removed.
 *
 * `assetKind` is the kind a DER fault trigger pins (`builderModel.pinnedAssetKind`);
 * given one, the subject fields name it. Every other event ignores it.
 */
export function useEventAttributeFormatter() {
  const t = useTranslations('workflows.eventAttributes')
  return useCallback(
    (eventType: EventType, attributeKey: string, assetKind?: string) => {
      // `attributeKey` is a runtime string (a stored condition field, a template
      // path), so the literal-keyed maps are widened here — at the one index that
      // needs it — rather than in their declarations, which would drop the
      // catalog-coverage check for every event.
      const kindLabels: Record<string, Partial<Record<string, EventAttributeMessageKey>>> = DER_FAULT_ATTRIBUTE_LABEL_KEY
      const kindKey = eventType === 'der.fault' && assetKind ? kindLabels[assetKind]?.[attributeKey] : undefined
      if (kindKey) return t(kindKey)
      const labels: Record<string, EventAttributeMessageKey> = EVENT_ATTRIBUTE_LABEL_KEY[eventType]
      const key = labels[attributeKey]
      return key ? t(key) : attributeKey
    },
    [t]
  )
}

/**
 * Every valid leaf key under `workflows.contextFields`, typed the same way as
 * {@link EventAttributeMessageKey}.
 */
type ContextFieldMessageKey = MessageKeys<
  NestedValueOf<Messages, 'workflows.contextFields'>,
  NestedKeyOf<NestedValueOf<Messages, 'workflows.contextFields'>>
>

/** The name a resolved entity goes by, heading the fields it carries. */
const CONTEXT_ENTITY_LABEL_KEY = {
  charger: 'entity_charger',
  connector: 'entity_connector',
  site: 'entity_site',
  transaction: 'entity_transaction',
  vehicle: 'entity_vehicle',
  bess: 'entity_bess',
  generator: 'entity_generator',
  link: 'entity_link'
} as const satisfies Record<ContextEntityKind, ContextFieldMessageKey>

/**
 * The name an entity goes by on one event, where the event's own id makes it
 * something more specific than the entity kind.
 *
 * Vehicle telemetry reports a vehicle's home site — its `site_id` attribute is
 * labelled "Home site ID", and the site context resolves from exactly that id — so
 * calling the source "Site" there would leave an author guessing which site they
 * are reading. Every other event's site is the site the asset sits at, which the
 * kind already names.
 */
const CONTEXT_ENTITY_LABEL_OVERRIDE = {
  'vehicle.telemetry': { site: 'entity_homeSite' }
} as const satisfies Partial<Record<EventType, Partial<Record<ContextEntityKind, ContextFieldMessageKey>>>>

/**
 * When a conditionally-resolved entity is actually there, said as a condition to
 * check rather than a risk to fear. Keyed by entity kind: an entity is conditional
 * for one reason wherever it is conditional — the site because the event may carry
 * no site id, the transaction because there may be no session in progress, the
 * vehicle because the session's tag may map to none, the charger and link because
 * the connectivity feed reports on either, the connector because the event may name
 * no connector the charger lists.
 */
const CONTEXT_AVAILABILITY_KEY = {
  charger: 'availability_charger',
  connector: 'availability_connector',
  link: 'availability_link',
  site: 'availability_site',
  transaction: 'availability_transaction',
  vehicle: 'availability_vehicle',
  bess: 'availability_bess',
  generator: 'availability_generator'
} as const satisfies Partial<Record<ContextEntityKind, ContextFieldMessageKey>>

/**
 * Context-field labels, scoped per entity kind rather than per event: a resolved
 * charger is the same charger whichever event reached it, so its eleven fields are
 * named once here instead of repeated under every event that can reach one.
 *
 * Keyed by `ContextFieldKeyFor<K>` — the literal field keys the generated schema
 * declares for that entity — so this map must cover each entity's fields exactly:
 * omit one and it is a missing property naming the key, add one the schema does not
 * declare and it is an excess property. The outer mapping over `ContextEntityKind`
 * makes a newly reachable entity kind a compile error until it is registered.
 *
 * Labels are bare — the picker heading names the entity — and carry a unit where the
 * backing view reports one, matching how the event attributes read.
 */
const CONTEXT_FIELD_LABEL_KEY: { [K in ContextEntityKind]: Record<ContextFieldKeyFor<K>, ContextFieldMessageKey> } = {
  charger: {
    charger_id: 'charger_chargerId',
    charger_name: 'charger_chargerName',
    connectivity_status: 'charger_connectivityStatus',
    firmware_version: 'charger_firmwareVersion',
    last_heard_at: 'charger_lastHeardAt',
    max_power: 'charger_maxPower',
    model: 'charger_model',
    operational_status: 'charger_operationalStatus',
    site_id: 'charger_siteId',
    status: 'charger_status',
    vendor: 'charger_vendor'
  },
  connector: {
    charger_id: 'connector_chargerId',
    connector_id: 'connector_connectorId',
    connector_name: 'connector_connectorName',
    evse_id: 'connector_evseId'
  },
  site: {
    address: 'site_address',
    latitude: 'site_latitude',
    longitude: 'site_longitude',
    num_chargers: 'site_numChargers',
    num_vehicles: 'site_numVehicles',
    power_ceiling: 'site_powerCeiling',
    site_id: 'site_siteId',
    site_name: 'site_siteName',
    timezone: 'site_timezone'
  },
  transaction: {
    charger_id: 'transaction_chargerId',
    connector_id: 'transaction_connectorId',
    latest_soc: 'transaction_latestSoc',
    max_power_import: 'transaction_maxPowerImport',
    mean_power_import: 'transaction_meanPowerImport',
    ocpp_tag: 'transaction_ocppTag',
    plug_in_time: 'transaction_plugInTime',
    plug_out_time: 'transaction_plugOutTime',
    site_id: 'transaction_siteId',
    start_timestamp: 'transaction_startTimestamp',
    starting_soc: 'transaction_startingSoc',
    stop_timestamp: 'transaction_stopTimestamp',
    total_energy_exported: 'transaction_totalEnergyExported',
    total_energy_imported: 'transaction_totalEnergyImported',
    transaction_id: 'transaction_transactionId',
    transaction_pk: 'transaction_transactionPk',
    vehicle_id: 'transaction_vehicleId'
  },
  vehicle: {
    home_site_id: 'vehicle_homeSiteId',
    latitude: 'vehicle_latitude',
    longitude: 'vehicle_longitude',
    odometer: 'vehicle_odometer',
    organization_id: 'vehicle_organizationId',
    remaining_range: 'vehicle_remainingRange',
    state_of_charge: 'vehicle_stateOfCharge',
    vehicle_id: 'vehicle_vehicleId',
    vehicle_name: 'vehicle_vehicleName',
    vehicle_status: 'vehicle_vehicleStatus'
  },
  bess: {
    available_charge_energy: 'bess_availableChargeEnergy',
    available_discharge_energy: 'bess_availableDischargeEnergy',
    bess_id: 'bess_bessId',
    bess_name: 'bess_bessName',
    charge_power_limit: 'bess_chargePowerLimit',
    control_mode: 'bess_controlMode',
    discharge_power_limit: 'bess_dischargePowerLimit',
    energy_capacity: 'bess_energyCapacity',
    firmware_version: 'bess_firmwareVersion',
    grid_mode: 'bess_gridMode',
    last_heard_at: 'bess_lastHeardAt',
    manufacturer: 'bess_manufacturer',
    max_power: 'bess_maxPower',
    model: 'bess_model',
    run_state: 'bess_runState',
    site_id: 'bess_siteId',
    state_of_charge: 'bess_stateOfCharge',
    state_of_health: 'bess_stateOfHealth'
  },
  generator: {
    control_mode: 'generator_controlMode',
    firmware_version: 'generator_firmwareVersion',
    fuel_type: 'generator_fuelType',
    generator_id: 'generator_generatorId',
    generator_name: 'generator_generatorName',
    grid_mode: 'generator_gridMode',
    last_heard_at: 'generator_lastHeardAt',
    manufacturer: 'generator_manufacturer',
    max_power: 'generator_maxPower',
    model: 'generator_model',
    power_output: 'generator_powerOutput',
    rated_apparent_power: 'generator_ratedApparentPower',
    rated_power: 'generator_ratedPower',
    run_state: 'generator_runState',
    site_id: 'generator_siteId'
  },
  link: {
    connectivity_status: 'link_connectivityStatus',
    description: 'link_description',
    last_heard_at: 'link_lastHeardAt',
    link_id: 'link_linkId',
    name: 'link_name',
    nickname: 'link_nickname',
    site_id: 'link_siteId'
  }
}

/** A DER asset's run state, shared by the BESS and generator views. */
const RUN_STATE_LABEL_KEY = {
  NORMAL: 'runState_normal',
  DEGRADED: 'runState_degraded',
  CRITICAL: 'runState_critical'
} as const satisfies Record<RunState, ContextFieldMessageKey>

/** A link's connectivity, as the connectivity feed reports it. */
const CONNECTIVITY_LABEL_KEY = {
  Online: 'connectivity_online',
  Offline: 'connectivity_offline'
} as const satisfies Record<ContextEnumValueFor<'link', 'connectivity_status'>, ContextFieldMessageKey>

/** A vocabulary one or more live enum fields draw their labels from. */
type ContextEnumVocabulary = 'runState' | 'fuelType' | 'connectivity'

/**
 * Which vocabulary labels a live entity's enum field. The backend declares the
 * members; this says what they are called, so a condition's value picker reads
 * "Degraded" or "Natural Gas" rather than the wire constant.
 */
const CONTEXT_ENUM_VOCABULARY = {
  bess: { run_state: 'runState' },
  generator: { run_state: 'runState', fuel_type: 'fuelType' },
  link: { connectivity_status: 'connectivity' }
} as const satisfies { [K in ContextEntityKind]?: Partial<Record<ContextFieldKeyFor<K>, ContextEnumVocabulary>> }

/**
 * Resolves a context entity kind to its localized name — the source row the picker
 * files that entity's fields under, and the qualifier a standalone field label
 * carries.
 *
 * `eventType` is the trigger's effective event; given one, an entity that event
 * names more specifically takes that name (see {@link CONTEXT_ENTITY_LABEL_OVERRIDE}).
 */
export function useContextEntityFormatter() {
  const t = useTranslations('workflows.contextFields')
  return useCallback(
    (entity: string, eventType?: EventType | null) => {
      // `entity` is a runtime string (a stored condition field, a template path), so
      // the literal-keyed maps are widened here rather than in their declarations.
      const overrides: Record<string, Partial<Record<string, ContextFieldMessageKey>> | undefined> =
        CONTEXT_ENTITY_LABEL_OVERRIDE
      const override = eventType ? overrides[eventType]?.[entity] : undefined
      if (override) return t(override)
      const labels: Record<string, ContextFieldMessageKey | undefined> = CONTEXT_ENTITY_LABEL_KEY
      const key = labels[entity]
      return key ? t(key) : entity
    },
    [t]
  )
}

/**
 * When a conditionally-resolved entity is present, or `''` for one that always is.
 * The picker shows it on the entity's source row; nothing else needs it.
 */
export function useContextAvailabilityFormatter() {
  const t = useTranslations('workflows.contextFields')
  return useCallback((entity: string) => {
    const keys: Record<string, ContextFieldMessageKey | undefined> = CONTEXT_AVAILABILITY_KEY
    const key = keys[entity]
    return key ? t(key) : ''
  }, [t])
}

/**
 * Resolves one value of a live entity's enum field to its localized label, or the
 * raw value when its vocabulary does not know it — a member the backend added since.
 * `value` is a runtime string (a stored condition value), so each vocabulary checks
 * membership before it is trusted as its literal union.
 */
export function useContextEnumValueFormatter() {
  const t = useTranslations('workflows.contextFields')
  const fuelType = useGeneratorFuelTypeFormatter()
  return useCallback(
    (entity: string, field: string, value: string) => {
      const byEntity: Record<string, Partial<Record<string, ContextEnumVocabulary>> | undefined> =
        CONTEXT_ENUM_VOCABULARY
      switch (byEntity[entity]?.[field]) {
        case 'runState':
          return isRunState(value) ? t(RUN_STATE_LABEL_KEY[value]) : value
        case 'connectivity':
          return isConnectivity(value) ? t(CONNECTIVITY_LABEL_KEY[value]) : value
        case 'fuelType':
          return isGeneratorFuelType(value) ? fuelType(value) : value
        default:
          return value
      }
    },
    [t, fuelType]
  )
}

function isRunState(value: string): value is RunState {
  return Object.hasOwn(RUN_STATE_LABEL_KEY, value)
}

function isConnectivity(value: string): value is keyof typeof CONNECTIVITY_LABEL_KEY {
  return Object.hasOwn(CONNECTIVITY_LABEL_KEY, value)
}

const GENERATOR_FUEL_TYPES = ['DIESEL', 'NATURAL_GAS', 'LP_VAPOR', 'GASOLINE'] as const satisfies readonly FuelType[]

function isGeneratorFuelType(value: string): value is FuelType {
  return (GENERATOR_FUEL_TYPES as readonly string[]).includes(value)
}

/**
 * Resolves one field of a resolved entity to its localized display label. Every
 * field the generated schema declares has one, enforced by
 * `CONTEXT_FIELD_LABEL_KEY`'s type, so the raw-key fallback is reachable only for a
 * path the catalog cannot name — one authored outside the builder, or against an
 * entity since dropped.
 */
export function useContextFieldFormatter() {
  const t = useTranslations('workflows.contextFields')
  return useCallback((entity: string, field: string) => {
    const byEntity: Record<string, Record<string, ContextFieldMessageKey> | undefined> = CONTEXT_FIELD_LABEL_KEY
    const key = byEntity[entity]?.[field]
    return key ? t(key) : field
  }, [t])
}

/**
 * Every valid leaf key under `workflows.actionResults`, typed the same way as
 * {@link EventAttributeMessageKey} so a typo'd/renamed value is a compile error
 * at the map below and `t(key)` needs no cast.
 */
type ActionResultFieldMessageKey = MessageKeys<
  NestedValueOf<Messages, 'workflows.actionResults'>,
  NestedKeyOf<NestedValueOf<Messages, 'workflows.actionResults'>>
>

/**
 * Result-field labels, scoped per action (a bare field key like `applied` is
 * action-specific), mirroring {@link EVENT_ATTRIBUTE_LABEL_KEY}. Only the scalar
 * fields the token palette offers (see `templatableResultFields`) need a label;
 * actions with no offered fields map to `{}` and the formatter falls back to the
 * raw key. `Record<ActionId, …>` (not `satisfies`) keeps the value type wide
 * enough to index by a runtime field key while making a new action a compile
 * error until it's registered.
 */
const ACTION_RESULT_FIELD_LABEL_KEY: Record<ActionId, Record<string, ActionResultFieldMessageKey>> = {
  'charger.reset': {
    charger_id: 'chargerReset_chargerId',
    applied: 'chargerReset_applied',
    status_code: 'chargerReset_statusCode'
  },
  'charger.remote_start': {
    charger_id: 'chargerRemoteStart_chargerId',
    connector_id: 'chargerRemoteStart_connectorId',
    tag_id: 'chargerRemoteStart_tagId',
    transaction_id: 'chargerRemoteStart_transactionId',
    communication_result: 'chargerRemoteStart_communicationResult',
    status_code: 'chargerRemoteStart_statusCode'
  },
  'report.run': {
    report_format: 'reportRun_reportFormat'
  },
  'email.users': {},
  'sms.send': {},
  'notification.create': {},
  wait: {},
  // fetch.data results nest one level (see `ACTION_RESULT_CATALOG`), so keys here
  // are the dotted `<entity>.<field>` paths the palette offers as tokens.
  'fetch.data': {
    'charger.charger_id': 'fetchData_chargerId',
    'charger.charger_name': 'fetchData_chargerName',
    'charger.site_id': 'fetchData_chargerSiteId',
    'charger.status': 'fetchData_chargerStatus',
    'charger.operational_status': 'fetchData_chargerOperationalStatus',
    'charger.connectivity_status': 'fetchData_chargerConnectivityStatus',
    'charger.vendor': 'fetchData_chargerVendor',
    'charger.model': 'fetchData_chargerModel',
    'charger.firmware_version': 'fetchData_chargerFirmwareVersion',
    'charger.max_power': 'fetchData_chargerMaxPower',
    'charger.last_heard_at': 'fetchData_chargerLastHeardAt',
    'vehicle.vehicle_id': 'fetchData_vehicleId',
    'vehicle.vehicle_name': 'fetchData_vehicleName',
    'vehicle.organization_id': 'fetchData_vehicleOrganizationId',
    'vehicle.state_of_charge': 'fetchData_vehicleStateOfCharge',
    'vehicle.vehicle_status': 'fetchData_vehicleStatus',
    'vehicle.latitude': 'fetchData_vehicleLatitude',
    'vehicle.longitude': 'fetchData_vehicleLongitude',
    'vehicle.remaining_range': 'fetchData_vehicleRemainingRange',
    'vehicle.odometer': 'fetchData_vehicleOdometer',
    'vehicle.home_site_id': 'fetchData_vehicleHomeSiteId',
    'site.site_id': 'fetchData_siteId',
    'site.site_name': 'fetchData_siteName',
    'site.timezone': 'fetchData_siteTimezone',
    'site.power_ceiling': 'fetchData_sitePowerCeiling',
    'site.num_chargers': 'fetchData_siteNumChargers',
    'site.num_vehicles': 'fetchData_siteNumVehicles',
    'site.latitude': 'fetchData_siteLatitude',
    'site.longitude': 'fetchData_siteLongitude',
    'site.address': 'fetchData_siteAddress',
    retrieved_at: 'fetchData_retrievedAt'
  }
}

/**
 * Resolves an action's result field (scoped to its action) to a localized label
 * under `workflows.actionResults`, falling back to the raw field key when no
 * entry exists (a field with no offered label yet).
 */
function useActionResultFieldFormatter() {
  const t = useTranslations('workflows.actionResults')
  return useCallback(
    (action: ActionId, field: string) => {
      const key = ACTION_RESULT_FIELD_LABEL_KEY[action][field]
      return key ? t(key) : field
    },
    [t]
  )
}

type ActionLabelKey =
  | 'action_email'
  | 'action_sms'
  | 'action_wait'
  | 'action_report'
  | 'action_charger_reboot'
  | 'action_charger_remote_start'
  | 'action_notification'
  | 'action_fetch'

/** Locale key (in the `workflows.builder` namespace) per action identifier. */
export const ACTION_LABEL_KEY: Record<ActionId, ActionLabelKey> = {
  'email.users': 'action_email',
  'sms.send': 'action_sms',
  'notification.create': 'action_notification',
  wait: 'action_wait',
  'report.run': 'action_report',
  'charger.reset': 'action_charger_reboot',
  'charger.remote_start': 'action_charger_remote_start',
  'fetch.data': 'action_fetch'
}

/** Label formatter for action identifiers (the builder's node titles). */
function useActionLabelFormatter() {
  const t = useTranslations('workflows.builder')
  return useCallback((action: ActionId) => t(ACTION_LABEL_KEY[action]), [t])
}

type ActionDescriptionKey =
  | 'actionDescription_email'
  | 'actionDescription_sms'
  | 'actionDescription_wait'
  | 'actionDescription_report'
  | 'actionDescription_charger_reboot'
  | 'actionDescription_charger_remote_start'
  | 'actionDescription_notification'
  | 'actionDescription_fetch'

/** One-sentence description key (in the `workflows.builder` namespace) per action identifier. */
const ACTION_DESCRIPTION_KEY: Record<ActionId, ActionDescriptionKey> = {
  'email.users': 'actionDescription_email',
  'sms.send': 'actionDescription_sms',
  'notification.create': 'actionDescription_notification',
  wait: 'actionDescription_wait',
  'report.run': 'actionDescription_report',
  'charger.reset': 'actionDescription_charger_reboot',
  'charger.remote_start': 'actionDescription_charger_remote_start',
  'fetch.data': 'actionDescription_fetch'
}

/** Description formatter for action identifiers (picker rows and the inspector's chosen-action card). */
function useActionDescriptionFormatter() {
  const t = useTranslations('workflows.builder')
  return useCallback((action: ActionId) => t(ACTION_DESCRIPTION_KEY[action]), [t])
}

type LogLevelLabelKey =
  | 'Inspector_logLevel_debug'
  | 'Inspector_logLevel_info'
  | 'Inspector_logLevel_warning'
  | 'Inspector_logLevel_error'

/** Locale key (in the `workflows.runs` namespace) per run-log severity level. */
const LOG_LEVEL_LABEL_KEY: Record<LogLevel, LogLevelLabelKey> = {
  DEBUG: 'Inspector_logLevel_debug',
  INFO: 'Inspector_logLevel_info',
  WARNING: 'Inspector_logLevel_warning',
  ERROR: 'Inspector_logLevel_error'
}

/** Label formatter for run-step log severity levels. */
function useLogLevelFormatter() {
  const t = useTranslations('workflows.runs')
  return useCallback((level: LogLevel) => t(LOG_LEVEL_LABEL_KEY[level]), [t])
}

/** Label formatter for condition operators. */
function useOperatorFormatter() {
  const t = useTranslations('workflows.builder')
  return useCallback((operator: CatalogOperator) => t(`operator_${operator}`), [t])
}

/** Label formatter for wait-step duration units. */
function useDurationUnitFormatter() {
  const t = useTranslations('workflows.builder')
  return useCallback((unit: DurationUnit) => t(`durationUnit_${unit}`), [t])
}

/** Label formatter for in-app notification priority levels. */
function useNotificationPriorityFormatter() {
  const t = useTranslations('workflows.builder')
  return useCallback((priority: NotificationPriority) => t(`notificationPriority_${priority}`), [t])
}

/** Label formatter for report types. */
function useReportTypeFormatter() {
  const t = useTranslations('workflows.builder')
  return useCallback((reportType: RunReportParametersAuthoring['reportType']) => t(`reportType_${reportType}`), [t])
}

/** Label formatter for workflow template gallery categories. */
function useTemplateCategoryFormatter() {
  const t = useTranslations('workflows.templates')
  return useCallback((category: WorkflowTemplateCategory) => t(`category_${category}`), [t])
}

/**
 * Pluralized duration label ("5 minutes", "1 day"). Accepts any of the catalog's
 * unit spellings — `DurationUnit` (wait step, uppercase), `WindowUnit` (temporal,
 * lowercase), or `RelativeUnit` (report ranges) — and normalizes to the lowercase
 * unit the `durationLabel` ICU message selects on, so pluralization stays in the
 * locale files.
 */
function useWorkflowDurationFormatter() {
  const t = useTranslations('workflows.builder')
  return useCallback(
    (value: number, unit: DurationUnit | WindowUnit | RelativeUnit) =>
      t('durationLabel', { count: value, unit: unit.toLowerCase() }),
    [t]
  )
}

function formatValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ')
  return String(value ?? '')
}

/**
 * `RAISED` → `Raised` for display; the stored/wire value stays the untouched
 * all-caps original. Scoped to the attributes the catalog marks
 * {@link CatalogAttribute.titleCased}, so every other event's enum values keep
 * rendering exactly as authored. Shared across every place a condition value
 * reaches the user: the condition editor's dropdowns, the inspector/canvas
 * summaries, and the template gallery preview.
 */
export function enumOptionLabel(attribute: CatalogAttribute | undefined, value: string): string {
  return attribute?.titleCased ? `${value.charAt(0)}${value.slice(1).toLowerCase()}` : value
}

/**
 * One-line summary of a trigger's condition spec ("Status Equals Faulted and …").
 * Field names resolve through the event catalog's attribute labels. A flat spec
 * is a single condition; a grouped spec AND-combines its groups, each rendered
 * with its own AND/OR logic (so an OR group isn't misread as "and"). Returns
 * `undefined` when the spec has no conditions to describe.
 */
function useConditionSpecFormatter() {
  const t = useTranslations('workflows.builder')
  const formatOperator = useOperatorFormatter()
  const formatAttribute = useEventAttributeFormatter()

  return useCallback(
    (spec: GroupedConditionSpec | FlatConditionSpec, eventType: EventType): string | undefined => {
      // A value that is exactly one `{{…}}` reference is a comparison against
      // another field, so it reads as that field's label — the raw token tells a
      // gallery reader nothing. Falls back to the bare path when this event
      // doesn't name it. A plain value renders through `enumOptionLabel`, which
      // only changes anything for the DER fault severity/transition fields.
      const valueText = (value: unknown, attribute: CatalogAttribute | undefined): string => {
        if (Array.isArray(value)) return value.map((entry) => enumOptionLabel(attribute, formatValue(entry))).join(', ')
        if (typeof value !== 'string') return formatValue(value)
        const path = wholeReferencePath(value)
        if (path === undefined) return enumOptionLabel(attribute, value)
        const key = attributeKeyFromField(path)
        return getEventType(eventType)?.attributes.some((a) => a.key === key) ? formatAttribute(eventType, key) : path
      }

      const conditionText = (condition: Condition): string => {
        const attr = getEventType(eventType)?.attributes.find(
          (a) => `${EVENT_ATTRIBUTE_PREFIX}${a.key}` === condition.field
        )
        const field = attr ? formatAttribute(eventType, attr.key) : condition.field.replace(EVENT_ATTRIBUTE_PREFIX, '')
        const operator = formatOperator(condition.operator)
        if (condition.operator === 'IS_TRUE' || condition.operator === 'IS_FALSE') {
          return t('summary_conditionNoValue', { field, operator })
        }
        return t('summary_condition', { field, operator, value: valueText(condition.value, attr) })
      }

      const joinConditions = (conditions: Condition[], logic: BooleanLogic) =>
        conditions.map(conditionText).join(` ${t(logic === 'OR' ? 'summary_or' : 'summary_and')} `)

      if ('groups' in spec) {
        const groups = spec.groups.filter((group) => group.conditions.length > 0)
        if (groups.length === 0) return undefined
        return groups.map((group) => joinConditions(group.conditions, group.logic ?? 'AND')).join(` ${t('summary_and')} `)
      }
      return conditionText(spec)
    },
    [t, formatOperator, formatAttribute]
  )
}

/**
 * The workflows-domain formatters, grouped so callers reach them via
 * `useFormatters().workflows.*`. Add new workflow formatters here as they land.
 */
export default function useWorkflowsFormatters() {
  const actionLabel = useActionLabelFormatter()
  const actionDescription = useActionDescriptionFormatter()
  const actionResultField = useActionResultFieldFormatter()
  const conditionSpec = useConditionSpecFormatter()
  const contextAvailability = useContextAvailabilityFormatter()
  const contextEntity = useContextEntityFormatter()
  const contextEnumValue = useContextEnumValueFormatter()
  const contextField = useContextFieldFormatter()
  const duration = useWorkflowDurationFormatter()
  const durationUnit = useDurationUnitFormatter()
  const triggerCadence = useTriggerCadenceFormatter()
  const eventAttribute = useEventAttributeFormatter()
  const logLevel = useLogLevelFormatter()
  const notificationPriority = useNotificationPriorityFormatter()
  const operator = useOperatorFormatter()
  const reportType = useReportTypeFormatter()
  const templateCategory = useTemplateCategoryFormatter()
  const triggerLabel = useTriggerLabelFormatter()
  const triggerDescription = useTriggerDescriptionFormatter()
  const triggerComparison = useTriggerComparisonFormatter()
  return useMemo(
    () => ({
      actionLabel,
      actionDescription,
      actionResultField,
      conditionSpec,
      contextAvailability,
      contextEntity,
      contextEnumValue,
      contextField,
      duration,
      durationUnit,
      triggerCadence,
      eventAttribute,
      logLevel,
      notificationPriority,
      operator,
      reportType,
      templateCategory,
      triggerLabel,
      triggerDescription,
      triggerComparison
    }),
    [
      actionLabel,
      actionDescription,
      actionResultField,
      conditionSpec,
      contextAvailability,
      contextEntity,
      contextEnumValue,
      contextField,
      duration,
      durationUnit,
      triggerCadence,
      eventAttribute,
      logLevel,
      notificationPriority,
      operator,
      reportType,
      templateCategory,
      triggerLabel,
      triggerDescription,
      triggerComparison
    ]
  )
}
